import React from 'react';
import { Box, BoxProps, Divider } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ScrollableView } from '../components';
import { Button } from '../../Button';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useDownBreakpoint } from '@uplift/hooks';


const ReviewLayoutContentControlsContext = React.createContext<{
  onNext: (() => void) | null;
  onPrev: (() => void) | null;
} | null>(null);


export const useReviewLayoutContentControlsContext = () => React.useContext(ReviewLayoutContentControlsContext);


export function ReviewLayoutContentControlsProvider<T>({ selectedItem, onSelectItem, items, findSelectedIndex, ...props }: {
  items: T[];
  findSelectedIndex: (item: T) => boolean;
  selectedItem?: T;
  onSelectItem: (item: T) => void;
  children?: any;
}) {
  const currentSelectedIndex = React.useMemo(() => {
    if (!selectedItem) {
      return null;
    }
    if (!items) {
      return null;
    }

    return items.findIndex(findSelectedIndex);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedItem, items ]);

  const onPrevSelect = React.useMemo(() => {
    if (currentSelectedIndex === null) return null;
    if (currentSelectedIndex === 0) return null;

    return () => onSelectItem(items[currentSelectedIndex - 1]);
  }, [ currentSelectedIndex, items, onSelectItem ]);

  const onNextSelect = React.useMemo(() => {
    if (currentSelectedIndex === null) return null;
    if (currentSelectedIndex >= items.length - 1) return null;

    return () => onSelectItem(items[currentSelectedIndex + 1]);
  }, [ currentSelectedIndex, items, onSelectItem ]);

  return (
    <ReviewLayoutContentControlsContext.Provider
      value={{
        onPrev: onPrevSelect,
        onNext: onNextSelect,
      }}
      {...props}
    />
  );
}


export const ReviewLayoutContentControls = () => {
  const controlsContext = React.useContext(ReviewLayoutContentControlsContext);

  return (
    <Box>
      <Divider />
      <Box boxSizing="border-box" p={2} display="flex" justifyContent="space-between">
        <Button onClick={controlsContext?.onPrev} disabled={!controlsContext?.onPrev} color="secondary" startIcon={<ArrowBack />}>Previous</Button>
        <Button onClick={controlsContext?.onNext} disabled={!controlsContext?.onNext} color="secondary" endIcon={<ArrowForward />}>Next</Button>
      </Box>
    </Box>
  );
};


const useStyles = makeStyles<Partial<ReviewLayoutProps>>({ name: 'ReviewLayout' })(theme => ({
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: theme.palette.grey[50],
  },
  sidebar: {
    flex: 1,
    backgroundColor: theme.palette.common.white,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  borderedScrollBar: {
    '& ::-webkit-scrollbar': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    '& ::-webkit-scrollbar-thumb': {
      borderRight: 'solid 2px transparent',
      borderLeft: 'solid 3px transparent',
    },

  }
}));


export type ReviewLayoutProps = {
  sidebar: React.ReactNode;
  sidebarWidth?: BoxProps['width'];
  minSidebarWidth?: BoxProps['minWidth'];
  content: React.ReactNode;
  contentFooter?: React.ReactNode;
  additionalContent?: React.ReactNode;
  classes?: Partial<typeof useStyles>;
};

export const ReviewLayout: React.FC<ReviewLayoutProps> = (props) => {
  const { classes } = useStyles(props);
  const lgDown = useDownBreakpoint('lg');

  return (
    <Box className={classes.root}>
      <Box display="flex" flex={1}>
        <Box className={classes.sidebar} maxWidth={props.sidebarWidth ?? '384px'} minWidth={props.minSidebarWidth ?? '0px'}>
          <ScrollableView className={classes.borderedScrollBar} width="100%" height="100%">
            {props.sidebar}
          </ScrollableView>
        </Box>
        {props.content}
        {(props.additionalContent && !lgDown) ? props.additionalContent : null}
      </Box>
    </Box>
  );
};
